// ============================================================================
// FACILITATOR SWIM-LANE VIEW — monthly bookings grouped by facilitator
// One row per facilitator, days of the month as columns, bars for bookings.
// Bar fill = room colour, left border = booking status, label = customer name,
// falling back to room name if a sanitised view ever omits the customer.
// ============================================================================

import React, { useMemo } from 'react';
import {
  StudioBooking,
  FacilitatorSummary,
  BookingStatus,
  DATE_TYPE_LABELS,
  STATUS_LABELS,
  TIER_LABELS,
  FACILITATOR_TBC_ID,
  AvailabilitySlot,
} from '../../services/studio-bookings';
import { DEFAULT_ROOM_COLOR } from '../../constants/roomColors';
import { parseYMD } from '../../pages/StudioCalendar';

const DAY_WIDTH = 70;
const LABEL_WIDTH = 180;
const BAR_HEIGHT = 26;
const BAR_GAP = 4;
const LANE_PADDING = 6;
const UNAVAILABLE_STYLE: React.CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(135deg, rgba(148,163,184,0.28) 0 4px, transparent 4px 9px)',
};

const STATUS_BORDER_COLORS: Record<BookingStatus, string> = {
  approved:  '#16a34a', // green-600
  pending:   '#f59e0b', // amber-500
  rejected:  '#dc2626', // red-600
  cancelled: '#94a3b8', // slate-400
};

type FacilitatorRole = 'primary' | 'secondary';

interface PositionedBooking {
  booking: StudioBooking;
  startDay: number; // 1-indexed day-of-month, clipped to month bounds
  endDay: number;
  track: number;
  role: FacilitatorRole;
}

function clipToMonth(booking: StudioBooking, year: number, month: number): { startDay: number; endDay: number } | null {
  const daysInMonth = new Date(year, month, 0).getDate();
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month - 1, daysInMonth);
  const bStart = parseYMD(booking.startDate);
  const bEnd = parseYMD(booking.endDate);
  const clippedStart = bStart < monthStart ? monthStart : bStart;
  const clippedEnd = bEnd > monthEnd ? monthEnd : bEnd;
  if (clippedStart > clippedEnd) return null;
  return { startDay: clippedStart.getDate(), endDay: clippedEnd.getDate() };
}

function isHalfDay(booking: StudioBooking): boolean {
  return booking.dateType === 'morning' || booking.dateType === 'afternoon';
}

function bookingsOverlap(a: PositionedBooking, b: PositionedBooking): boolean {
  if (a.endDay < b.startDay || b.endDay < a.startDay) return false;
  const sameDay = a.startDay === a.endDay && b.startDay === b.endDay && a.startDay === b.startDay;
  if (sameDay && isHalfDay(a.booking) && isHalfDay(b.booking) && a.booking.dateType !== b.booking.dateType) {
    return false; // morning + afternoon on the same day don't conflict
  }
  return true;
}

function assignTracks(items: PositionedBooking[]): PositionedBooking[] {
  const sorted = [...items].sort((x, y) => x.startDay - y.startDay || x.endDay - y.endDay);
  const tracks: PositionedBooking[][] = [];
  for (const item of sorted) {
    const track = tracks.find(t => !t.some(existing => bookingsOverlap(existing, item)));
    if (track) {
      item.track = tracks.indexOf(track);
      track.push(item);
    } else {
      item.track = tracks.length;
      tracks.push([item]);
    }
  }
  return sorted;
}

function dayShadeClass(isWeekend: boolean, isHoliday: boolean, isBlocked: boolean): string {
  if (isBlocked) return 'bg-slate-200/60';
  if (isWeekend) return 'bg-slate-100/70';
  if (isHoliday) return 'bg-blue-50/60';
  return '';
}

interface BookingBarProps {
  positioned: PositionedBooking;
  roomColor: string;
  onSelect: (b: StudioBooking) => void;
}

function BookingBar({ positioned, roomColor, onSelect }: BookingBarProps) {
  const { booking, startDay, endDay, track, role } = positioned;
  const half = isHalfDay(booking);
  let left = (startDay - 1) * DAY_WIDTH;
  let width = (endDay - startDay + 1) * DAY_WIDTH - 4;
  if (half) {
    width = DAY_WIDTH / 2 - 4;
    if (booking.dateType === 'afternoon') left += DAY_WIDTH / 2;
  }
  const top = LANE_PADDING + track * (BAR_HEIGHT + BAR_GAP);
  const dateLabel = booking.startDate === booking.endDate
    ? booking.startDate
    : `${booking.startDate} → ${booking.endDate}`;
  const faded = booking.status === 'rejected' || booking.status === 'cancelled';
  const isSecondary = role === 'secondary';
  const roleSuffix = isSecondary ? ' · secondary facilitator' : '';

  const style: React.CSSProperties = isSecondary
    ? {
        left, width, top, height: BAR_HEIGHT,
        backgroundColor: '#ffffff',
        color: roomColor,
        border: `2px solid ${roomColor}`,
      }
    : {
        left, width, top, height: BAR_HEIGHT,
        backgroundColor: roomColor,
        color: '#ffffff',
        borderLeft: `4px solid ${STATUS_BORDER_COLORS[booking.status]}`,
      };

  return (
    <button
      onClick={() => onSelect(booking)}
      title={`${booking.customer || booking.facilitatorName} · ${booking.roomName} · ${DATE_TYPE_LABELS[booking.dateType]} · ${STATUS_LABELS[booking.status]} · ${dateLabel}${roleSuffix}`}
      className={`absolute text-[11px] font-semibold px-1.5 rounded truncate text-left leading-[24px] hover:brightness-95 transition-[filter] ${faded ? 'opacity-60' : ''}`}
      style={style}
    >
      {isSecondary && (
        <span
          className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: STATUS_BORDER_COLORS[booking.status] }}
        />
      )}
      {/* defensive: customer is whitelisted for guests, but a sanitised
          projection could omit it again if that decision is revisited */}
      {booking.customer || booking.roomName}
    </button>
  );
}

interface LaneRowProps {
  matchFacilitatorId: string;
  label: string;
  sublabel: string;
  bookings: StudioBooking[];
  year: number;
  month: number;
  roomColorMap: Record<string, string>;
  onSelectBooking: (b: StudioBooking) => void;
  dashed?: boolean;
  offboarded?: boolean;
  unavailable?: Record<string, AvailabilitySlot>;  // date -> unavailable slot
}

function LaneRow({ matchFacilitatorId, label, sublabel, bookings, year, month, roomColorMap, onSelectBooking, dashed, offboarded, unavailable }: LaneRowProps) {
  const daysInMonth = new Date(year, month, 0).getDate();

  const positioned = useMemo(() => {
    const items: PositionedBooking[] = [];
    for (const booking of bookings) {
      let role: FacilitatorRole;
      if (booking.facilitatorId === matchFacilitatorId) role = 'primary';
      else if (booking.secondaryFacilitatorIds?.includes(matchFacilitatorId)) role = 'secondary';
      else continue;
      const span = clipToMonth(booking, year, month);
      if (!span) continue;
      items.push({ booking, startDay: span.startDay, endDay: span.endDay, track: 0, role });
    }
    return assignTracks(items);
  }, [bookings, matchFacilitatorId, year, month]);

  const numTracks = Math.max(1, ...positioned.map(p => p.track + 1));
  const laneHeight = LANE_PADDING * 2 + numTracks * BAR_HEIGHT + (numTracks - 1) * BAR_GAP;

  return (
    <div className={`flex border-b border-[#e2e8f0] ${dashed ? 'bg-slate-50/50' : ''} ${offboarded ? 'opacity-50 grayscale-[40%]' : ''}`}>
      <div
        className={`flex-shrink-0 border-r border-[#e2e8f0] p-2.5 sticky left-0 z-20 flex flex-col justify-center ${dashed ? 'bg-slate-50/50' : 'bg-white'}`}
        style={{ width: LABEL_WIDTH }}
      >
        <p className={`text-sm font-semibold truncate flex items-center gap-1.5 ${dashed ? 'text-slate-500 italic' : 'text-navy'}`}>
          {label}
          {offboarded && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500">
              Offboarded
            </span>
          )}
        </p>
        <p className="text-[10px] text-slate-400">
          {sublabel} · {positioned.length} {positioned.length === 1 ? 'booking' : 'bookings'}
        </p>
      </div>
      <div className="relative flex-shrink-0" style={{ width: daysInMonth * DAY_WIDTH, height: laneHeight }}>
        {Object.entries(unavailable || {}).map(([date, slot]) => {
          const [y, m, d] = date.split('-').map(Number);
          if (y !== year || m !== month) return null;
          const left = (d - 1) * DAY_WIDTH + (slot === 'afternoon' ? DAY_WIDTH / 2 : 0);
          const width = slot === 'full_day' ? DAY_WIDTH : DAY_WIDTH / 2;
          return (
            <div
              key={date}
              className="absolute inset-y-0"
              style={{ ...UNAVAILABLE_STYLE, left, width }}
              title={`Unavailable — ${slot === 'full_day' ? 'all day' : slot}`}
            />
          );
        })}
        {positioned.map(p => (
          <BookingBar
            key={p.booking.bookingId}
            positioned={p}
            roomColor={roomColorMap[p.booking.roomId] || DEFAULT_ROOM_COLOR}
            onSelect={onSelectBooking}
          />
        ))}
      </div>
    </div>
  );
}

interface FacilitatorSwimlaneViewProps {
  bookings: StudioBooking[];
  facilitators: FacilitatorSummary[];
  selectedFacilitatorIds: Set<string>;
  onToggleFacilitator: (userId: string) => void;
  onSetFacilitatorIds: (ids: Set<string>) => void;
  year: number;
  month: number;
  holidays: Set<string>;
  blockedDaySet: Set<string>;
  roomColorMap: Record<string, string>;
  onSelectBooking: (b: StudioBooking) => void;
  unavailableByUser?: Record<string, Record<string, AvailabilitySlot>>;
}

export function FacilitatorSwimlaneView({
  bookings,
  facilitators,
  selectedFacilitatorIds,
  onToggleFacilitator,
  onSetFacilitatorIds,
  year,
  month,
  holidays,
  blockedDaySet,
  roomColorMap,
  onSelectBooking,
  unavailableByUser,
}: FacilitatorSwimlaneViewProps) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;

  const selectedFacilitators = useMemo(
    () => facilitators
      .filter(f => selectedFacilitatorIds.has(f.userId))
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [facilitators, selectedFacilitatorIds]
  );

  const tbcCount = useMemo(
    () => bookings.filter(b => b.facilitatorId === FACILITATOR_TBC_ID && clipToMonth(b, year, month)).length,
    [bookings, year, month]
  );
  const hasRows = selectedFacilitators.length > 0 || tbcCount > 0;

  function dayYMD(day: number): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}`;
  }

  function dayFlags(day: number) {
    const ymd = dayYMD(day);
    const date = new Date(year, month - 1, day);
    return {
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isHoliday: holidays.has(ymd),
      isBlocked: blockedDaySet.has(ymd),
    };
  }

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      {/* Facilitator filter bar */}
      <div className="p-4 border-b border-[#e2e8f0] flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mr-1">Facilitators</span>
        {facilitators
          .slice()
          .sort((a, b) => a.displayName.localeCompare(b.displayName))
          .map(f => {
            const selected = selectedFacilitatorIds.has(f.userId);
            return (
              <button
                key={f.userId}
                onClick={() => onToggleFacilitator(f.userId)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  f.isOffboarded ? 'opacity-50' : ''
                } ${
                  selected
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-white border-[#e2e8f0] text-slate-500 hover:bg-slate-50'
                }`}
              >
                {f.displayName}
                <span className="text-[10px] opacity-60">{TIER_LABELS[f.tier]}</span>
              </button>
            );
          })}
        <div className="flex items-center gap-1 ml-auto text-xs">
          <button
            onClick={() => onSetFacilitatorIds(new Set(facilitators.filter(f => f.tier === 'tier1').map(f => f.userId)))}
            className="px-2 py-1 rounded-lg text-slate-500 hover:bg-slate-50 font-semibold"
          >
            Tier 1 only
          </button>
          <button
            onClick={() => onSetFacilitatorIds(new Set(facilitators.map(f => f.userId)))}
            className="px-2 py-1 rounded-lg text-slate-500 hover:bg-slate-50 font-semibold"
          >
            All
          </button>
          <button
            onClick={() => onSetFacilitatorIds(new Set())}
            className="px-2 py-1 rounded-lg text-slate-500 hover:bg-slate-50 font-semibold"
          >
            None
          </button>
        </div>
      </div>

      {!hasRows ? (
        <div className="p-12 text-center">
          <p className="text-sm text-slate-500">No facilitators selected.</p>
          <p className="text-xs text-slate-400 mt-1">Pick one or more facilitators above to see their bookings.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            {/* Day header row */}
            <div className="flex border-b border-[#e2e8f0] sticky top-0 bg-white z-20">
              <div
                className="flex-shrink-0 border-r border-[#e2e8f0] sticky left-0 bg-white z-10"
                style={{ width: LABEL_WIDTH }}
              />
              {days.map(day => {
                const { isWeekend, isHoliday, isBlocked } = dayFlags(day);
                const isToday = isCurrentMonth && day === today.getDate();
                return (
                  <div
                    key={day}
                    className={`flex-shrink-0 flex flex-col items-center justify-center py-1.5 text-xs font-bold bg-white ${dayShadeClass(isWeekend, isHoliday, isBlocked)} ${isToday ? 'text-primary' : 'text-slate-400'}`}
                    style={{ width: DAY_WIDTH }}
                    title={isBlocked ? 'Blocked day' : isHoliday ? 'Irish public holiday' : undefined}
                  >
                    {day}
                  </div>
                );
              })}
            </div>

            {/* Weekend / holiday / blocked column shading, spans behind all rows */}
            <div className="relative">
              <div className="absolute inset-0 flex pointer-events-none z-0" style={{ left: LABEL_WIDTH }}>
                {days.map(day => {
                  const { isWeekend, isHoliday, isBlocked } = dayFlags(day);
                  return (
                    <div key={day} className={`flex-shrink-0 ${dayShadeClass(isWeekend, isHoliday, isBlocked)}`} style={{ width: DAY_WIDTH }} />
                  );
                })}
              </div>

              <div className="relative z-10">
                {/* Facilitator rows */}
                {selectedFacilitators.map(f => (
                  <LaneRow
                    key={f.userId}
                    matchFacilitatorId={f.userId}
                    label={f.displayName}
                    sublabel={TIER_LABELS[f.tier]}
                    bookings={bookings}
                    year={year}
                    month={month}
                    roomColorMap={roomColorMap}
                    onSelectBooking={onSelectBooking}
                    offboarded={f.isOffboarded}
                    unavailable={unavailableByUser?.[f.userId]}
                  />
                ))}

                {/* TBC row — pinned to the bottom */}
                {tbcCount > 0 && (
                  <LaneRow
                    matchFacilitatorId={FACILITATOR_TBC_ID}
                    label="TBC"
                    sublabel="Unassigned"
                    bookings={bookings}
                    year={year}
                    month={month}
                    roomColorMap={roomColorMap}
                    onSelectBooking={onSelectBooking}
                    dashed
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
